import { FormattedNumber } from '@sm/intl';

import { ComparePlanIds, PackageIds, Packages, SkuTypes } from '~shared/constants/pricing';
import {
  INDIVIDUAL_PACKAGES_WITH_FORMS,
  INDIVIDUAL_PACKAGES_WITH_FORMS_PARTIAL,
  INDIVIDUAL_PACKAGES_WITH_FORMS_REVERSED,
  INDIVIDUAL_PACKAGES_WITH_FORMS_PARTIAL_REVERSED,
  INDIVIDUAL_PACKAGES_WITH_STARTER,
  ROUTE_PACKAGE_MAP,
  TEAMS_PACKAGES,
} from './constants';

import {
  PackageData,
  PackageDataSet,
  PackageSet,
  PackageSkuCost,
  PackageSkuCosts,
  PackageType,
} from '~shared/types/pricing/package';
import { DiscountInfo, Feature, FeatureSetMap } from '~shared/types/pricing/pricingsvc';
import { PlaceHolderType } from './types';
import { ExperimentAssignment } from '~shared/types/pricing/experiments';
import { EXPERIMENT_IDENTIFIERS } from '~server/routes/Pricing/experimentsConfig';

/**
 * Creates a className friendly string from a package label
 *
 * @param {PlaceHolderType} packageObj The package object to create the class name from
 * @return {string} The label name, ready to be used as a className
 */
export function packageLabelToClassName(packageObj: PlaceHolderType): string {
  return packageObj.label.trim().replace(/\s/g, '-').toLowerCase();
}

/**
 * Returns a package's SKU cost object for a particular SKU type
 *
 * @param skuCosts The package's SKU costs
 * @param skuType The type of SKU to find
 * @returns The SkuCost object of the requested type or undefined if not found
 */
export function getSkuCostByType(skuCosts: PackageSkuCost[], skuType: SkuTypes): PackageSkuCost | undefined {
  const skuCostsArr = Array.from(skuCosts);
  return skuCostsArr.find(sku => sku.skuType === skuType);
}

/**
 *
 * @param skuCosts
 * @returns The package set cost information based on the subscription type
 */
export function getCoreCostFromSkus(skuCosts: PackageSkuCosts): PackageSkuCost | undefined {
  return getSkuCostByType(skuCosts, SkuTypes.CoreSeat);
}

/**
 *
 * @param packages
 * @returns The package set cost information replicated from one of the other comparison packages
 */

export function getCoreCostReplicaForBasicPackage(packages: PackageDataSet): PackageSkuCost | undefined {
  const referencePackageSkuCost = packages.find(packageData => packageData?.skuCost.length)?.skuCost[0];
  return referencePackageSkuCost
    ? {
        skuType: referencePackageSkuCost?.skuType,
        cost: 0,
        currency: referencePackageSkuCost?.currency,
      }
    : undefined;
}

/**
 * Given two skuCosts, returns the rounded percentage savings between them
 *
 * @param {object} skuCostOriginal The originally priced skuCost object
 * @param {object} skuCostDiscounted The discounted priced skuCost object
 */
export function calculateSavingsPercent(skuCostOriginal: PackageSkuCost, skuCostDiscounted: PackageSkuCost): number {
  let retVal = 0;
  const costDifference = skuCostOriginal?.cost - skuCostDiscounted?.cost;

  // If the "discounted" price is greater, we'll just ignore that...
  if (costDifference > 0) {
    retVal = Math.floor((costDifference / skuCostOriginal?.cost) * 100);
  }

  return retVal;
}

export function formatCostInSkuCurrency(
  value: number,
  skuCost: PackageSkuCost,
  minimumFractionDigits = 0
): React.ReactElement | string {
  return FormattedNumber({
    value,
    formatStyle: 'currency',
    currency: skuCost?.currency?.code,
    currencyDisplay: 'symbol',
    minimumFractionDigits,
  });
}

export function formatCostInLocaleCurrency(
  skuCost: PackageSkuCost,
  minimumFractionDigits = 0
): React.ReactElement | string {
  return formatCostInSkuCurrency(skuCost.cost / 100, skuCost, minimumFractionDigits);
}

export function monthlyCostInDollars(skuCost: PackageSkuCost): number {
  return skuCost.cost / 100 / 12;
}

export function formatMonthlyCostInLocaleCurrency(skuCost: PackageSkuCost): React.ReactElement | string {
  const monthlyCost = monthlyCostInDollars(skuCost);
  return formatCostInSkuCurrency(monthlyCost, skuCost);
}

export function getDiscountedSkuCost(skuCost: PackageSkuCost, discountInfo: DiscountInfo): PackageSkuCost {
  const tempSkuCost = { ...skuCost };
  tempSkuCost.cost = (skuCost.cost * (100 - discountInfo.discountPercentage)) / 100;
  return tempSkuCost;
}

export function createPackagesList(packages: PackageDataSet, packageSet: PackageSet): PackageDataSet {
  // The provided package list is both the filter and determines the order
  return packages
    .filter((packageObj: PackageData) => packageSet.includes(packageObj.packageName))
    .sort((a, b) => packageSet.indexOf(a.packageName) - packageSet.indexOf(b.packageName));
}

export function convertFeatureSetToMap(featureSet: Feature[]): FeatureSetMap {
  return featureSet?.reduce((acc: FeatureSetMap, feature: Feature) => {
    acc[feature.name] = feature;
    return acc;
  }, {});
}

/**
 * Get PackageSet based on package type (route) and if starter is enabled for user
 * @param packageType
 * @param isStarterEnabled (optional)
 * @returns PackageSet
 */

export function getPackageSetBasedOnPath(
  packageType: string,
  isForms?: ExperimentAssignment | null,
  isStarterEnabled = false
): PackageSet {
  if (isForms && packageType === PackageType.Individual) {
    if (isStarterEnabled) {
      return isForms?.hasAssignment && isForms.treatmentName === EXPERIMENT_IDENTIFIERS.GROW_2529_FORMS.treatment2
        ? INDIVIDUAL_PACKAGES_WITH_FORMS_REVERSED
        : INDIVIDUAL_PACKAGES_WITH_FORMS;
    }
    return isForms?.hasAssignment && isForms.treatmentName === EXPERIMENT_IDENTIFIERS.GROW_2529_FORMS.treatment2
      ? INDIVIDUAL_PACKAGES_WITH_FORMS_PARTIAL_REVERSED
      : INDIVIDUAL_PACKAGES_WITH_FORMS_PARTIAL;
  }
  if (isStarterEnabled && packageType === PackageType.Individual) {
    return INDIVIDUAL_PACKAGES_WITH_STARTER;
  }
  return ROUTE_PACKAGE_MAP[packageType] ?? TEAMS_PACKAGES;
}

export function getPackagesBasedOnQueryStringIds(ids: string, packageType: string): PackageSet {
  const packages = ids
    .split(',')
    .map(id => {
      return Object.keys(PackageIds).find((key: string): boolean => {
        return PackageIds[key as keyof typeof PackageIds] === Number(id);
      });
    })
    .filter(packageName => packageName && ComparePlanIds.includes(packageName)) as PackageSet;

  return packages?.length ? packages : getPackageSetBasedOnPath(packageType);
}

export function isPaidUser(pkg: number | string): boolean {
  return Number(pkg) !== PackageIds[Packages.Basic];
}
